/**
 * Event tracker: forms (form_start, form_submit, search, view_search_results).
 * @package AdvancedTrackingManager
 */

import type { AtmConfig } from '@lib/types';
import { getPageTitle, sendEvent } from './core';

const w = globalThis as unknown as Window;

type FormLike = HTMLFormElement & { action?: string; id?: string; name?: string; method?: string };

const getFormSource = (form: FormLike): string => {
  if (!form) return 'generic';
  const action = (form.action ?? '').toLowerCase();
  const id = (form.id ?? '').toLowerCase();
  const name = (form.name ?? '').toLowerCase();
  const className = (typeof form.className === 'string' ? form.className : '').toLowerCase();
  const el = form as HTMLElement;
  if (
    (el.dataset?.formId ?? el.dataset?.portalId) ||
    action.includes('hubspot') ||
    action.includes('hs-') ||
    className.includes('hubspot') ||
    className.includes('hs-form')
  ) {
    return 'hubspot';
  }
  if (
    id.startsWith('nf-form-') ||
    className.includes('ninja-forms-form') ||
    name.startsWith('nf_')
  ) {
    return 'ninja_forms';
  }
  if (id.startsWith('forminator-form-') || className.includes('forminator-form')) {
    return 'forminator';
  }
  return 'generic';
};

const getFormPayload = (form: FormLike): Record<string, unknown> => {
  const formId = form.id ?? '';
  const formName = form.name ?? '';
  const formAction = form.action ?? '';
  const formMethod = (form.method ?? 'get').toLowerCase();
  const fields = form.querySelectorAll('input, select, textarea');
  const fieldNames: string[] = [];
  for (const f of fields) {
    const field = f as HTMLInputElement;
    if (field.name && !fieldNames.includes(field.name)) fieldNames.push(field.name);
  }
  return {
    page_title: getPageTitle(),
    form_id: formId,
    form_name: formName,
    form_action: formAction,
    form_method: formMethod,
    form_source: getFormSource(form),
    form_fields: fieldNames,
  };
};

export const trackFormStart = (config: AtmConfig, enable: { formStart: boolean }): void => {
  if (!enable.formStart) return;
  const formsStarted: Record<string, boolean> = {};
  const handler = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    const form = target?.closest?.('form') ?? null;
    if (!form || Object.hasOwn(form.dataset ?? {}, 'atmExclude')) return;
    const key =
      (form as HTMLFormElement).id ||
      (form as HTMLFormElement).name ||
      (form as HTMLFormElement).action ||
      form.getAttribute('action') ||
      '';
    const formKey = key || `form_${form.getBoundingClientRect?.()?.top ?? 0}`;
    if (formsStarted[formKey]) return;
    formsStarted[formKey] = true;
    sendEvent('form_start', getFormPayload(form as FormLike), config);
  };
  document.addEventListener('focus', handler, true);
  document.addEventListener('input', handler, true);
};

export const trackFormSubmissions = (config: AtmConfig, enable: { formSubmit: boolean }): void => {
  if (!enable.formSubmit) return;
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || Object.hasOwn(form.dataset ?? {}, 'atmExclude')) return;
      sendEvent('form_submit', getFormPayload(form as FormLike), config);
    },
    true
  );
};

export const trackSearch = (
  config: AtmConfig,
  enable: { search: boolean },
  searchParamName: string
): void => {
  if (!enable.search) return;
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || Object.hasOwn(form.dataset ?? {}, 'atmExclude')) return;
      const searchInput =
        form.querySelector<HTMLInputElement>(
          `input[name="s"], input[name="search"], input[type="search"], input[name="${searchParamName}"]`
        ) ?? form.querySelector<HTMLInputElement>(`input[name='${searchParamName}']`);
      if (!searchInput) return;
      const term = (searchInput.value ?? '').trim();
      if (!term) return;
      sendEvent('search', { search_term: term, page_title: getPageTitle() }, config);
    },
    true
  );
};

export const trackViewSearchResults = (
  config: AtmConfig,
  enable: { viewSearchResults: boolean },
  searchParamName: string
): void => {
  if (!enable.viewSearchResults) return;
  try {
    const params = new URLSearchParams(w.location.search);
    const term = params.get(searchParamName);
    if (term !== null && term !== undefined && term.trim() !== '') {
      sendEvent(
        'view_search_results',
        { search_term: term.trim(), page_title: getPageTitle() },
        config
      );
    }
  } catch {
    // ignore
  }
};
